import React, { useState, useMemo } from 'react';
import FormGroup from '@mui/material/FormGroup';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import RolesAndPermissionsPicker from '../../../rolesAndPermissionsPicker/RolesAndPermissionsPicker';
import useSnackbar from '../../../hooks/useSnackbar';

const AddUserModal = ({ shown, handleHide, onSubmit, isFirstUser = null }) => {
    const [emailAddress, setEmailAddress] = useState('');
    const [name, setName] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const { showSnackbar } = useSnackbar();

    const emailPattern = /^[A-Za-z0-9\p{L}\p{N}!#$%&'+/=?^_{|}~-]+(?:\.[A-Za-z0-9\p{L}\p{N}!#$%&'*+/=?^_{|}~-]+)*@(?:[A-Za-z0-9\p{L}\p{N}.-]+)\.[A-Za-z\p{L}]{2,63}$/u;

    const isEmailValid = useMemo(() => emailPattern.test(emailAddress.trim()), [emailAddress]);

    const handleInvite = async (e) => {
        e.preventDefault();

        if (!isEmailValid) {
            setEmailTouched(true);
            showSnackbar('Please enter a valid email address', 'error');
            return;
        }

        const user = {
            emailAddress,
            name,
            roleId: selectedRoleId,
            permissions: selectedPermissions
        };

        setIsLoading(true);
        await onSubmit(user);

        setEmailAddress('');
        setName('');
        setSelectedRoleId(null);
        setSelectedPermissions([]);
        setIsLoading(false);
        setEmailTouched(false);
    };

    return (
        <Dialog open={shown} onClose={handleHide} maxWidth="md" fullWidth>
            <DialogTitle>Invite user</DialogTitle>
            <DialogContent dividers>
                <DialogContentText>
                    Please enter the email address of the new user. This email address will receive an invitation to join the account.
                </DialogContentText>
                <FormGroup>
                    <TextField
                        fullWidth
                        margin="dense"
                        required
                        type="email"
                        variant="outlined"
                        size="small"
                        label="Email"
                        autoComplete="off"
                        value={emailAddress}
                        onChange={(e) => {
                            setEmailAddress(e.target.value);
                            if (!emailTouched) setEmailTouched(true);
                        }}
                        onBlur={() => setEmailTouched(true)}
                        placeholder="name@example.com"
                        error={emailTouched && !isEmailValid}
                        helperText={emailTouched && !isEmailValid ? 'Enter a valid email address' : null}
                    />
                </FormGroup>
                <FormGroup>
                    <TextField
                        fullWidth
                        type="text"
                        variant="outlined"
                        size="small"
                        label="Name"
                        autoComplete="off"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </FormGroup>
                <RolesAndPermissionsPicker
                    selectedRoleId={selectedRoleId}
                    selectedPermissions={selectedPermissions}
                    setSelectedRoleId={setSelectedRoleId}
                    setSelectedPermissions={setSelectedPermissions}
                    isFirstUser={isFirstUser}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={!isEmailValid || isLoading}
                    onClick={handleInvite}
                    endIcon={isLoading ? <CircularProgress size={16} style={{ color: '#FFF' }} /> : null}
                >
                    {isLoading ? 'Working' : 'Invite & Add More'}
                </Button>
                <Button
                    disabled={!isEmailValid || isLoading}
                    onClick={(e) => {
                        handleInvite(e);
                        handleHide();
                    }}
                    endIcon={isLoading ? <CircularProgress size={16} style={{ color: '#FFF' }} /> : null}
                >
                    {isLoading ? 'Working' : 'Invite & Close'}
                </Button>
                <Button onClick={handleHide} variant="outlined" disabled={isLoading}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddUserModal;
